/* eslint-disable @next/next/no-img-element */
"use client";
import React, { useCallback } from "react";
import { Checkbox } from "@mui/material";
import { useAddWishlistMutation, useDeleteWishlistMutation } from "@/service/wishlist";
import { useAppDispatch, useAppSelector } from "@/lib/hook";
import {
  addToWishlistState,
  removeFromWishlistState,
  getWishlistProductIds,
} from "@/lib/slices/wishlistSlice";
import { getToken } from "@/lib/slices/authSlice";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import { getFromStorage } from "@/constants/storage";
import { STORAGE_KEYS } from "@/constants/storageKeys";

interface WishlistButtonProps {
  productId: number | string;
  storeId: number | string;
  isWishlist?: boolean;
  className?: string;
}

function WishlistButton({
  productId,
  storeId,
  isWishlist = false,
  className = "",
}: WishlistButtonProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [addWishlist, { isLoading }] = useAddWishlistMutation();
  const wishlistProductIds = useAppSelector(getWishlistProductIds);
  const reduxToken = useAppSelector(getToken);

  const [deleteWishlist, { isLoading: isDeleting }] = useDeleteWishlistMutation();
  
  // Local loading state for immediate feedback
  const [localLoading, setLocalLoading] = React.useState(false);

  // Check token from both Redux and localStorage
  const token = reduxToken || getFromStorage(STORAGE_KEYS.token);

  const isInWishlist =
    isWishlist || wishlistProductIds.includes(Number(productId));

  const isCurrentlyLoading = isLoading || isDeleting || localLoading;

  const handleWishlistToggle = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();

      // Prevent multiple clicks during loading
      if (isCurrentlyLoading) {
        return;
      }

      // Re-check token at the time of click
      const currentToken = reduxToken || getFromStorage(STORAGE_KEYS.token);

      if (!currentToken) {
        toast.error("Please login to add items to wishlist");
        router.push("/auth/login");
        return;
      }

      if (!productId || !storeId) {
        toast.error("Product information is missing");
        return;
      }

      const newWishlistState = !isInWishlist;

      // Set local loading immediately for instant feedback
      setLocalLoading(true);

      // Optimistic update - happens immediately
      if (newWishlistState) {
        dispatch(addToWishlistState(Number(productId)));
      } else {
        dispatch(removeFromWishlistState(Number(productId)));
      }

      try {
        if (newWishlistState) {
          await addWishlist({
            product_id: productId,
            store_id: storeId,
            is_wishlist: true,
            type: "normal",
          }).unwrap();
          toast.success("Added to wishlist");
        } else {
          await deleteWishlist({
            product_id: productId,
            store_id: storeId,
            type: "normal",
          }).unwrap();
          toast.success("Removed from wishlist");
        }
      } catch (error: any) {
        // Revert optimistic update on error
        if (newWishlistState) {
          dispatch(removeFromWishlistState(Number(productId)));
        } else {
          dispatch(addToWishlistState(Number(productId)));
        }
        toast.error(error?.data?.message || "Failed to update wishlist");
      } finally {
        // Clear local loading state
        setLocalLoading(false);
      }
    },
    [addWishlist, deleteWishlist, dispatch, isInWishlist, productId, storeId, router, reduxToken, isCurrentlyLoading]
  );

  return (
    <Checkbox
      checked={isInWishlist}
      onChange={handleWishlistToggle}
      onClick={(e) => e.stopPropagation()}
      disabled={isCurrentlyLoading}
      className={className}
      sx={{
        padding: 0,
        opacity: isCurrentlyLoading ? 0.7 : 1,
        cursor: isCurrentlyLoading ? "not-allowed" : "pointer",
        transition: "opacity 0.2s ease",
        "& .MuiSvgIcon-root": {
          display: "none"
        },
        "& .MuiCheckbox-root": {
          padding: 0
        }
      }}
      icon={<img src="/images/heart_outline_icon.svg" alt="unchecked" style={{ display: "block" }} />}
      checkedIcon={<img src="/images/heart_filled_icon.svg" alt="checked" style={{ display: "block" }} />}
    />
  );
}

export default WishlistButton;

